/* eslint-disable eqeqeq */
import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import type { AppDispatch } from 'app/store';
import { ahuComponentFormsApi, type IMasterRow } from 'app/services/ahuComponentFormsApi';
import { ahuBoardBus } from '../bus/ahuBoardBus';
import { ResultsFilter } from './resultsFilter';
import type { CatalogueFieldConfig, ShimCatalogueField } from './componentFormTypes';
import type { FormModel } from './autoSaveModel';

/**
 * Phase 6e2a — CatalogueField engine (§49.4, catalogue_select_field.js read
 * at the load-bearing sites; parity 312/313). One engine per master-pick
 * field. The btn-sync toggle here emits the §11.1
 * `rotate:canvas:<componentName>:table` channel (CompTable sender half) and
 * consumes `choose:<componentName>:master` → selectModelWithID; CompTable is
 * the CatalogueSelector singleton's half — the same surface for every field,
 * accessory and component alike, keyed on componentName.
 *
 * Fidelity notes (code-verbatim):
 *  - selectModel: overrides = paramsFields where row value truthy and != the
 *    current filter value, set silent → ONE model.set({targetField,
 *    paramsSourceField JSON}) → `select:model:for_<modelId>` → +1 s
 *    minRequiredLengthChange.
 *  - deselectModel: clears the filter; motor_power special-reset (fan motor
 *    field: motor_power + 3 distances null); model.set({target:null, params
 *    JSON}); `deselct:model:<id>` [sic] → +1 s minRequiredLengthChange.
 *  - checkIfThereIsOnlyOneFoundModel: master_filter_id/master_frame_id NEVER
 *    auto-select outside rating (`!view.rate` early return); count==1 →
 *    first-detailed select (master_filter clears width/height_nominal_value
 *    first; same-id → silent params re-sync only); count==0 && selected →
 *    fan/motor surgical params-JSON key deletion, others clear targetField
 *    when filter dirty or withRating.
 *  - counts: filtered = GET <sourceURL>count/?<filters>; full = unfiltered
 *    count, and `full = filtered` when the filter is empty ([NEG]#118 port —
 *    no eval-as-API).
 *  - badge: "<count> / <full>", parent classes selected / allow_reset
 *    (count ≤ 1) / warning (count == 0) — rendered by CatalogueChrome.
 */

export class CatalogueFieldEngine {
  readonly config: CatalogueFieldConfig;

  /** results_filters[i] — index 0 = main (allowed_fields = paramsFields). */
  filters: ResultsFilter[];

  cmsCount: number | undefined = undefined;

  cmsFullCount: number | undefined = undefined;

  /** `filtersQuery()` as it was when `cmsCount` was last written (refreshCounts). */
  private countedFilters: string | null = null;

  /** Monotonic refreshCounts ticket — only the newest one may write `cmsCount`. */
  private countSeq = 0;

  /** Newest resetFilter per filter index that arrived while the lock was held. */
  private pendingResets = new Map<
    number,
    {
      attrs: Record<string, unknown>;
      silent: boolean;
      updateIfSame: boolean;
    }
  >();

  selected = false;

  selectedModel: IMasterRow | null = null;

  /** User-edited params marker (changedModel dirty flag, §49.4). */
  filterDirty = false;

  skipFilteredCountOnInit: boolean;

  /**
   * "There is nothing to count yet" — the owning form's say-so, re-evaluated
   * on every pass (filter form: no bank configuration chosen). Unlike
   * `skipFilteredCountOnInit` this is not a one-shot init flag: while it is
   * set, `refreshCounts` keeps `cmsCount` UNDEFINED (badge blank) instead of
   * asking for a count whose answer is a foregone 0, and clears a count left
   * over from before. The unfiltered total still loads, so the badge reads
   * " / 355" rather than " / ".
   */
  suspendCount = false;

  /**
   * "…and what it is waiting for is never coming" — set by the owning VIEW,
   * which is the half that knows (the filter dialog: the bank-configuration
   * list came back empty, so no bank configuration will ever be selected for
   * this filter). It overrides `suspendCount`, so the count IS fetched and the
   * badge shows whatever the API says, 0 included. Suspension is for the
   * window before a selection, not for a filter that has nothing to select.
   */
  countGateResolved = false;

  /** §50.2 #8 — explicit 6e2c surgery seam (filter selector smart-list). */
  readonly catalogueSelectorSeam = {
    catalogueRowsContainer: null as HTMLElement | null,
    mainRotationElement: null as HTMLElement | null,
    with_rating: false,
  };

  private model: FormModel;

  private dispatch: AppDispatch;

  private viewRef: { rate: boolean; withRating: boolean; render(): void };

  private subscribers = new Set<() => void>();

  private version = 0;

  private offs: Array<() => void> = [];

  private destroyed = false;

  /** onSelectChange / onChange callbacks (per-type wiring, §50.1).
   *
   * QA 2026-09-16 — these are ACCESSORS, and the ctor warm-up below is
   * replayable, because legacy took both as constructor arguments
   * (catalogue_select_field.js:52-53/174) and so could never fire the
   * warm-up before the per-type wiring existed. React attaches the wiring
   * from an effect, which runs AFTER the render that built the engine, so
   * the warm-up promise wins the race whenever `getFirstModelDetailed`
   * resolves in a microtask — i.e. whenever RTK Query already has the
   * detailed row cached, which is every re-open of a form. Missing that one
   * call left the eliminator's frame catalogue without its `eliminator_id`
   * secondary filter; see the header note on the warm-up block. */
  private _onSelectChange: ((selected?: boolean) => void) | null = null;

  private _onChange:
    | ((overrides?: Record<string, unknown>, deselected?: boolean) => void)
    | null = null;

  /** ctor warm-up state — `pending` once the detailed row has landed, and
   * one latch per callback so each replay fires exactly once. */
  private warmUp = { pending: false, changeSent: false, selectSent: false };

  get onSelectChange(): ((selected?: boolean) => void) | null {
    return this._onSelectChange;
  }

  set onSelectChange(cb: ((selected?: boolean) => void) | null) {
    this._onSelectChange = cb;
    this.flushWarmUp();
  }

  get onChange():
    | ((overrides?: Record<string, unknown>, deselected?: boolean) => void)
    | null {
    return this._onChange;
  }

  set onChange(
    cb:
      | ((overrides?: Record<string, unknown>, deselected?: boolean) => void)
      | null
  ) {
    this._onChange = cb;
    this.flushWarmUp();
  }

  /** Deliver the ctor warm-up notification to whichever callbacks exist —
   * now if they were wired before the row landed, on assignment if after. */
  private flushWarmUp(): void {
    if (!this.warmUp.pending || this.destroyed) return;
    if (!this.warmUp.changeSent && this._onChange) {
      this.warmUp.changeSent = true;
      this._onChange(this.filters[0].toJSON());
    }
    if (!this.warmUp.selectSent && this._onSelectChange) {
      this.warmUp.selectSent = true;
      this._onSelectChange(true);
    }
  }

  constructor(args: {
    config: CatalogueFieldConfig;
    model: FormModel;
    dispatch: AppDispatch;
    viewRef: { rate: boolean; withRating: boolean; render(): void };
  }) {
    this.config = args.config;
    this.model = args.model;
    this.dispatch = args.dispatch;
    this.viewRef = args.viewRef;
    this.skipFilteredCountOnInit = !!args.config.skipFilteredCountOnInit;

    const count = Math.max(1, args.config.filtersCount ?? 1);
    this.filters = Array.from({ length: count }, (_v, i) =>
      i === 0
        ? new ResultsFilter({}, { allowed_fields: args.config.paramsFields })
        : new ResultsFilter()
    );

    // Legacy ctor: the filter component's main filter is pinned to cms_id 1
    // (catalogue_select_field.js:162) BEFORE the params seed.
    this.pinCmsId();
    // Seed filter[0] from the model's params JSON column (§49.4 ctor).
    this.filters[0].set(this.parseParams());
    this.selected = !!this.model.get(this.config.targetField);

    // model → filter backflow (change:<paramsSourceField> → resetFilter silent).
    // QA 2026-09-02 — this used to be a bare `filters[0].reset(...)`, which
    // swapped the attributes and stopped. Legacy is
    // `resetFilter(JSON.parse(model.get(paramsSourceField)), true)`
    // (catalogue_select_field.js:386-390), and resetFilter → updateFilter →
    // updateAndCheckCount(true) → checkIfThereIsOnlyOneFoundModel is the ONLY
    // route from "a params field was written" to "the catalogue narrowed to
    // one row, take it". Flattening it is why an accessory whose catalogue
    // resolves to a single master (light) never picked one up. The auto-pick
    // itself stays behind the userArmed gate below, so untouched forms are
    // still inert on mount.
    this.offs.push(
      this.model.onChange((changed) => {
        if (
          Object.prototype.hasOwnProperty.call(changed, this.config.paramsSourceField)
        ) {
          this.resetFilter(this.parseParams(), true);
          this.bump();
        }
      })
    );

    // CompTable sender-half return wire: choose:<componentName>:master.
    this.offs.push(
      ahuBoardBus.on(
        `choose:${this.config.componentName}:master` as `choose:${string}:master`,
        (...busArgs: unknown[]) => {
          const payload = (busArgs[0] ?? {}) as {
            id?: number | string;
            component_id?: number;
          };
          if (payload.component_id != null && payload.component_id !== this.model.id)
            return;
          if (payload.id != null) {
            // Smart-list row pick — user gesture.
            this.model.markUserAction();
            void this.selectModelWithID(Number(payload.id));
          }
        }
      )
    );

    // Selected master warm-up (§49.4: targetField set → selected=true +
    // getFirstModelDetailed warms selectedModel, then the legacy ctor calls
    // onChange(results_filter.toJSON()) + onSelectChange(true) so per-type
    // cross-filter wiring (eliminator→frame eliminator_id etc.) seeds its
    // SECONDARY filters at init — display-only; the auto-pick + save remain
    // gated until a user gesture).
    //
    // The notification is LATCHED rather than fired-and-forgotten: on a
    // re-opened form RTK Query serves the detailed row from cache, so this
    // `.then` runs a microtask after the render that built the engine —
    // before the per-type effect has assigned onChange/onSelectChange.
    // flushWarmUp() replays it on assignment (see the accessors above).
    if (this.selected) {
      void this.fetchFirstDetailed().then((row) => {
        if (this.destroyed) return;
        if (row) this.selectedModel = row;
        this.bump();
        this.warmUp.pending = true;
        this.flushWarmUp();
      });
    }
    void this.refreshCounts(true);
  }

  destroy(): void {
    this.destroyed = true;
    this.offs.forEach((off) => off());
    this.offs = [];
    this.subscribers.clear();
    this.pendingResets.clear();
  }

  subscribe = (cb: () => void): (() => void) => {
    this.subscribers.add(cb);
    return () => this.subscribers.delete(cb);
  };

  getVersion = (): number => this.version;

  private bump(): void {
    this.version += 1;
    this.subscribers.forEach((cb) => cb());
  }

  private parseParams(): Record<string, unknown> {
    try {
      return JSON.parse(String(this.model.get(this.config.paramsSourceField) ?? '{}'));
    } catch {
      return {};
    }
  }

  filtersQuery(): string {
    return this.filters
      .map((f) => f.toURL(false))
      .filter((s) => s)
      .join('&');
  }

  paramValue(field: string): unknown {
    return this.filters[0].attributes[field];
  }

  // -------------------------------------------------------------------------
  // counts (counter.js — RTKQ gives the in-flight dedup + URL cache)
  // -------------------------------------------------------------------------
  /** §50.2 #5d — the view's half of the count gate (see `countGateResolved`).
   * Re-runs the count on a change so the badge follows immediately. */
  resolveCountGate(resolved: boolean): void {
    if (this.countGateResolved === resolved) return;
    this.countGateResolved = resolved;
    void this.refreshCounts();
  }

  async refreshCounts(_initial = false): Promise<void> {
    // Legacy fetchCount: `if (self.noFetchCount) return;` — fan/coil/
    // gas_heater/ewheel/phx master fields NEVER fetch counts (verify
    // 6e2a-11: NEW eagerly hit energy_wheels/all/count which 500s).
    if (this.config.noFetchCount) return;
    // legacy counter.js gates at FETCH time: `if (skipFilteredCountOnInit)
    // return;` — the owning view lifts the flag AFTER its init filter pass
    // (filter form), so mount-time resetFilter passes stay count-less.
    if (this.skipFilteredCountOnInit) return;
    const filters = this.filtersQuery();
    // Ticket for this pass. Overlapping refreshes land in whatever order their
    // requests resolve — an earlier filter's count can arrive AFTER a later
    // one's (RTKQ serves a repeat filter from its 60 s cache almost at once,
    // and the two queries hit different-sized tables) — and the loser used to
    // be whatever wrote last. A superseded pass now drops its answer instead of
    // painting a count for a filter the form has already moved off.
    const seq = (this.countSeq += 1);
    if (this.suspendCount && !this.countGateResolved) {
      // Nothing to count yet. Taking the ticket above is the point: it also
      // supersedes a count already in flight, so an answer for the
      // pre-selection filter cannot land in the badge behind this.
      if (this.cmsCount !== undefined) {
        this.cmsCount = undefined;
        this.countedFilters = null;
        this.bump();
      }
      await this.refreshFullCount();
      return;
    }
    try {
      const count = await this.dispatch(
        ahuComponentFormsApi.endpoints.getCatalogCount.initiate(
          { sourceURL: this.config.sourceURL, filters },
          { subscribe: false }
        )
      ).unwrap();
      if (this.destroyed || seq !== this.countSeq) return;
      this.cmsCount = count;
      // Which filter string `cmsCount` actually belongs to. `cmsCount` alone
      // cannot say: the catch below leaves the PREVIOUS filter's value in place
      // (and the `seq` guard above drops a superseded answer rather than
      // writing it). Reading it as "the current filter's count" is what would
      // hand the Smart List a pagination bound belonging to another filter.
      this.countedFilters = filters;
      if (!filters) {
        // [NEG]#118 — full = filtered when the filter is empty.
        this.cmsFullCount = count;
      } else {
        await this.refreshFullCount();
        if (this.destroyed) return;
      }
      this.bump();
    } catch {
      /* count fetch failure leaves the badge stale (legacy refetch retries) */
    }
  }

  /** The unfiltered total, once per engine — it does not depend on the filter,
   * so a suspended pass still loads it and the badge can show "/ N". */
  private async refreshFullCount(): Promise<void> {
    if (this.cmsFullCount !== undefined) return;
    try {
      const full = await this.dispatch(
        ahuComponentFormsApi.endpoints.getCatalogCount.initiate(
          { sourceURL: this.config.sourceURL, filters: '' },
          { subscribe: false }
        )
      ).unwrap();
      if (this.destroyed || this.cmsFullCount !== undefined) return;
      this.cmsFullCount = full;
      this.bump();
    } catch {
      /* leaves the total blank; the next pass asks again */
    }
  }

  private async fetchFirstDetailed(): Promise<IMasterRow | null> {
    // Legacy getFirstModelDetailed fetches the DETAILED base
    // (selectedModelURL — e.g. cms .../energy_wheels/ NOT .../all/) with
    // page 1 + the live filters (cms.components.pages.js:250).
    try {
      return await this.dispatch(
        ahuComponentFormsApi.endpoints.getCatalogFirstDetailed.initiate(
          {
            sourceURL: this.config.selectedModelURL ?? this.config.sourceURL,
            filters: this.filtersQuery(),
          },
          { subscribe: false }
        )
      ).unwrap();
    } catch {
      return null;
    }
  }

  // -------------------------------------------------------------------------
  // filter writes
  // -------------------------------------------------------------------------
  /** Re-entrancy latch (legacy `_isResettingFilter`, §49.4). */
  private isResettingFilter = false;

  /**
   * Re-pins the filter component's `cms_id` (legacy ctor
   * catalogue_select_field.js:162) on filters[0].
   *
   * QA 2026-10-02 — the ctor pinned it ONCE, but every path that empties
   * filters[0] (`deselectModel`, i.e. the Reset button, and `resetFilter`'s
   * clear-then-set) threw it away with the rest of the params: after a Reset
   * the filter catalogue's count / page / first-detailed requests went out
   * without `cms_id`, and `filter_params` was rewritten without it, so it
   * never came back for the rest of the dialog. It is an invariant of the
   * field, not a seed value, so it is re-applied at both clear sites.
   */
  private pinCmsId(filter: ResultsFilter = this.filters[0]): void {
    if (this.config.componentsName === 'filters') {
      filter.set({ cms_id: 1 });
    }
  }

  /** UI param-field edit (postRenderingInitialization binding, §49.4).
   * Legacy updateFilter ALWAYS runs updateAndCheckCount(true) → the
   * auto-pick check; `silent` only gates updateParamsOnModel. */
  updateFilter(
    attrs: Record<string, unknown>,
    silent = false,
    filterIndex = 0
  ): void {
    if (Object.keys(attrs).length) {
      if (filterIndex === 0) this.filterDirty = true;
      this.filters[filterIndex].set(attrs);
    }
    if (this.onChange && filterIndex === 0) this.onChange(attrs);
    this.bump();
    // legacy `updateFilter` opens with `await self.resetCatalogue()`, BEFORE
    // `updateAndCheckCount` — but drops the rows one round trip LATER than this
    // does, on purpose. See announceFilterChange.
    this.announceFilterChange(true);
    void this.refreshCounts().then(async () => {
      await this.checkIfThereIsOnlyOneFoundModel(false);
      if (!silent) this.updateParamsOnModel();
      if (this.isResettingFilter) this.isResettingFilter = false;
      this.announceFilterChange(false);
      this.flushPendingResets();
    });
  }

  /** Replay the resets that arrived while the pass above held the lock. */
  private flushPendingResets(): void {
    if (this.destroyed || this.isResettingFilter || !this.pendingResets.size)
      return;
    const queued = [...this.pendingResets.entries()];
    this.pendingResets.clear();
    queued.forEach(([filterIndex, { attrs, silent, updateIfSame }]) => {
      this.resetFilter(attrs, silent, filterIndex, updateIfSame);
    });
  }

  /** The attributes `clear()` → `pinCmsId()` → `set(attrs)` would produce —
   * the same-attrs short-circuit in `resetFilter` compares against this. It is
   * that sequence, run on a throwaway filter in the same order (the pin FIRST,
   * so a blank `cms_id` in `attrs` drops it exactly as the real pass would),
   * rather than a second copy of the rules. */
  private predictedFilter(
    filter: ResultsFilter,
    attrs: Record<string, unknown>,
    filterIndex: number
  ): Record<string, unknown> {
    const probe = filter.emptyClone();
    if (filterIndex === 0) this.pinCmsId(probe);
    probe.set(attrs);
    return probe.toJSON();
  }

  /** resetFilter(attrs, silent, filterIndex) — shim member #6 (legacy
   * clear-then-updateFilter with the same-attrs short-circuit).
   *
   * `updateIfSame` is legacy's own fourth argument (catalogue_select_field.js)
   * and the owning form's way of saying "run the pass even if it changes
   * nothing". One caller needs it: the filter form's INIT pass, which is what
   * lifts `skipFilteredCountOnInit` and is therefore the only thing that ever
   * fetches those two counts. On a new framed row every attribute it sends is
   * blank, so the short-circuit would skip it and the frame badge would stay
   * blank until — or unless — the open-time Loading fill produced a real pass. */
  resetFilter(
    newAttributes: Record<string, unknown>,
    silent = false,
    filterIndex = 0,
    updateIfSame = false
  ): void {
    if (this.isResettingFilter) {
      // The lock exists to break the resetFilter → updateFilter →
      // updateParamsOnModel → `change:<params>` → resetFilter loop, but it
      // spans a COUNT ROUND TRIP (updateFilter clears it in refreshCounts'
      // `.then`), so legacy's bare `return` loses any filter write that lands
      // inside that window — permanently, filter and badge alike. The filter
      // dialog opens straight into it: FilterForm's init pass starts the first
      // count with `bank_configuration_id=null` (a value the CMS filter set
      // answers 0 for), the selector then auto-selects the first bank
      // configuration, and on a slow count that write was dropped — leaving the
      // badge reading "0 / 355" for the life of the dialog. Queue the newest
      // write per filter index instead and replay it when the pass finishes;
      // the replay hits the same-attributes early return below when nothing
      // really moved — which is what stops the loop the lock is for, and why
      // that comparison has to be made against what `set` would actually keep
      // (see `predictedFilter`) rather than against the raw argument.
      if (newAttributes)
        this.pendingResets.set(filterIndex, {
          attrs: newAttributes,
          silent,
          updateIfSame,
        });
      return;
    }
    const filter = this.filters[filterIndex];
    if (!filter || !newAttributes) return;
    const prev = filter.toJSON();
    // Compare against what the pass below would actually leave in the filter,
    // not against the raw argument. `clear()` + `set()` strips blanks and
    // anything outside `allowed_fields` (resultsFilter.ts), and the callers
    // routinely pass such keys — FilterForm sends `frame: undefined` whenever
    // the filter has no frame and a null `filter_id` on the frame side, the
    // params backflow replays a JSON column whose nulls were never in the
    // filter. Counting those keys made every such call "different", so a
    // filter that had not moved still got the whole pass: a catalogue count,
    // a Smart List clear-and-reload and an auto-pick check. On dialog open
    // that is also what the queued replay ran into (#2085 review follow-up).
    const next = this.predictedFilter(filter, newAttributes, filterIndex);
    const same =
      Object.keys(prev).length === Object.keys(next).length &&
      !Object.keys(prev).some((key) => next[key] != prev[key]);
    if (same && !updateIfSame) return;
    this.isResettingFilter = true;
    filter.clear();
    if (filterIndex === 0) this.pinCmsId();
    this.updateFilter(newAttributes, silent, filterIndex);
  }

  /** Writes the params JSON column; clears targetField when count≠1 and not
   * withRating (§49.4 updateParamsOnModel — verbatim incl. the same-value
   * early return that prevents the params-wipe loop, verify 6e2a-2). */
  private updateParamsOnModel(): void {
    const newVal = JSON.stringify(this.filters[0].toJSON());
    // eslint-disable-next-line eqeqeq
    if (newVal == this.model.get(this.config.paramsSourceField)) return;
    const data: Record<string, unknown> = {
      [this.config.paramsSourceField]: newVal,
    };
    if (
      !this.viewRef.withRating &&
      this.model.get(this.config.targetField) &&
      this.cmsCount != 1
    ) {
      data[this.config.targetField] = null;
    }
    this.model.set(data);
    this.selected = !!this.model.get(this.config.targetField);
    if (!this.selected) this.selectedModel = null;
  }

  // -------------------------------------------------------------------------
  // select / deselect (verbatim ports)
  // -------------------------------------------------------------------------
  selectModel(row: IMasterRow | null): void {
    const overrides: Record<string, unknown> = {};
    this.selected = true;
    if (row) {
      this.config.paramsFields.forEach((key) => {
        const v = row[key];
        if (v && v != this.filters[0].attributes[key]) overrides[key] = v;
      });
    }
    this.filters[0].set(overrides);
    this.model.set({
      [this.config.targetField]: row ? row.id : null,
      [this.config.paramsSourceField]: JSON.stringify(this.filters[0].toJSON()),
    });
    ahuBoardBus.emit(
      `select:model:for_${this.model.id}` as `select:model:for_${number}`,
      this.model.toJSON(),
      row
    );
    const modelChanged = !this.selectedModel || this.selectedModel.id != row?.id;
    this.selectedModel = row;
    this.bump();
    this.viewRef.render();
    if (modelChanged && this.onSelectChange) this.onSelectChange(true);
    if (this.onChange) this.onChange(overrides);
    window.setTimeout(() => ahuBoardBus.emit('minRequiredLengthChange'), 1000);
    void this.refreshCounts();
  }

  async selectModelWithID(id: number): Promise<void> {
    try {
      const row = await this.dispatch(
        ahuComponentFormsApi.endpoints.getCatalogFirstDetailed.initiate(
          { sourceURL: this.config.sourceURL, filters: `id=${id}` },
          { subscribe: false }
        )
      ).unwrap();
      if (!this.destroyed && row) this.selectModel(row);
    } catch {
      /* no-op */
    }
  }

  deselectModel(): void {
    // Reset button / selector reach-in — always a user gesture.
    this.model.markUserAction();
    this.selected = false;
    this.filters[0].clear();
    this.pinCmsId();
    if (this.model.get('motor_power')) {
      this.model.set({
        motor_power: null,
        [this.config.targetField]: null,
        distance_to_downstream_wall: null,
        distance_to_right_wall: null,
        distance_to_roof: null,
        [this.config.paramsSourceField]: JSON.stringify(this.filters[0].toJSON()),
      });
    } else {
      const data: Record<string, unknown> = {
        [this.config.targetField]: null,
        [this.config.paramsSourceField]: JSON.stringify(this.filters[0].toJSON()),
      };
      // 6e2b — accessory deselect extras (§49.4:836-848 / parity 312):
      // non-{viewport,switch,contactor,overload} accessory resets null the
      // size/distance set so the canvas shape collapses.
      if (this.config.accessoryResetExtras) {
        data.height = null;
        data.width = null;
        data.height_calc = null;
        data.width_calc = null;
        data.overall_width = null;
        data.overall_depth = null;
        data.distance_to_downstream_wall = null;
        data.distance_to_right_wall = null;
        data.distance_to_roof = null;
      }
      this.model.set(data);
    }
    // reset-click site extra (catalogue_select_field.js:616): accessory
    // non-{overload,contactor,switch,viewport} resets remove the canvas shape.
    if (this.config.accessoryResetExtras) {
      ahuBoardBus.emit('remove_accessory_from_canvas', this.model.id);
    }
    ahuBoardBus.emit(
      `deselct:model:${this.model.id}` as `deselct:model:${number}`,
      this.config.paramsSourceField
    );
    this.selectedModel = null;
    this.filterDirty = false;
    this.bump();
    // legacy `deselectModel` ends in `self.resetCatalogue().then(...)` — see
    // announceFilterChange.
    this.announceFilterChange(true);
    void this.refreshCounts().then(() => this.announceFilterChange(false));
    this.viewRef.render();
    if (this.onChange) this.onChange({}, true);
    if (this.onSelectChange) this.onSelectChange(false);
    window.setTimeout(() => ahuBoardBus.emit('minRequiredLengthChange'), 1000);
  }

  /** §49.4 — "selection collapses when filters narrow to 1" engine. */
  async checkIfThereIsOnlyOneFoundModel(force = false): Promise<void> {
    // DATA SAFETY (verify 6e2a-3 / deviation #6): the auto-pick runs only
    // after a USER gesture on this form — NEVER from init-time wiring. The
    // FormModel save gate is the second lock; this keeps even the in-memory
    // selection from drifting before the user touches anything.
    if (!force && !this.model.userArmed) return;
    if (
      !this.viewRef.rate &&
      (this.config.targetField === 'master_filter_id' ||
        this.config.targetField === 'master_frame_id')
    )
      return;
    if (force || this.cmsCount == 1) {
      const row = await this.fetchFirstDetailed();
      if (this.destroyed) return;
      if (this.config.targetField === 'master_filter_id') {
        this.filters[0].unset('width_nominal_value');
        this.filters[0].unset('height_nominal_value');
      }
      if (this.selected && row) {
        if (this.model.get(this.config.targetField) == row.id) {
          // Same model — silent params re-sync only.
          const overrides: Record<string, unknown> = {};
          this.config.paramsFields.forEach((key) => {
            overrides[key] = row[key];
          });
          this.filters[0].set(overrides);
          this.model.set(
            {
              [this.config.paramsSourceField]: JSON.stringify(
                this.filters[0].toJSON()
              ),
            },
            { silent: true }
          );
          this.selectedModel = row;
          this.bump();
          return;
        }
        this.selected = false;
        this.selectedModel = null;
      }
      this.selectModel(row);
    } else if (this.selected) {
      // count != 1 (deselect rules — fan/motor surgical params-key deletion)
      if (this.config.targetField === 'master_fan_id' && this.cmsCount == 0) {
        const params = this.parseParams();
        delete params.id;
        delete params.wheel_size_id;
        delete params.wheel_width_full_size;
        delete params.wheel_dia_percent_id;
        this.model.set({
          [this.config.targetField]: null,
          fan_params: JSON.stringify(params),
        });
      }
      if (this.config.targetField === 'master_motor_id' && this.cmsCount == 0) {
        const params = this.parseParams();
        [
          'id',
          'efficiency_id',
          'enclosure_id',
          'housing_id',
          'mfg_id',
          'mounting_id',
          'series_id',
          'shaft_grounding_id',
          'speed_nominal_id',
          'type_id',
        ].forEach((k) => delete params[k]);
        this.model.set({
          [this.config.targetField]: null,
          motor_params: JSON.stringify(params),
        });
      }
      if (
        this.config.targetField !== 'master_energy_wheel_id' &&
        this.config.targetField !== 'master_fan_id' &&
        this.config.targetField !== 'master_motor_id' &&
        ((this.cmsCount == 0 && this.filterDirty) || this.viewRef.withRating)
      ) {
        this.model.set({ [this.config.targetField]: null });
      }
      this.selected = false;
      this.selectedModel = null;
      this.bump();
      if (this.onSelectChange) this.onSelectChange();
    }
  }

  // -------------------------------------------------------------------------
  // smart-list toggle — CompTable sender half (the rotate channel + choose
  // wire are frozen).
  // -------------------------------------------------------------------------
  /** One page of the filtered catalogue, spn resolved (renderRow's
   * `spnCustomSourceField` swap, catalogue_selector.js:161-163). */
  private async loadCataloguePage(page: number): Promise<Record<string, unknown>[]> {
    const rows = await this.dispatch(
      ahuComponentFormsApi.endpoints.getCatalogPage.initiate(
        { sourceURL: this.config.sourceURL, page, filters: this.filtersQuery() },
        { subscribe: false }
      )
    ).unwrap();
    const spnField = this.config.spnCustomSourceField ?? 'spn';
    return rows.map((r) => ({ ...r, spn: r[spnField] ?? r.spn }));
  }

  /**
   * Legacy is one CatalogueSelector singleton shared by every catalogue field,
   * accessory and component alike (`catalogue_select_field.js:574` binds
   * `button.btn-sync[name=<componentName>]` on both) — so there is no
   * accessory/component split to pass on. `count` is the filtered cms_count
   * that bounds the scroll pagination (`has_available_pages`:
   * loaded_count < cms_count, cms.components.pages.js:152-155); `loadPage`
   * is the receiver's half of `loader.load_page`.
   */
  async toggleSmartList(): Promise<void> {
    try {
      const rows = await this.loadCataloguePage(1);
      if (this.destroyed) return;
      ahuBoardBus.emit(
        `rotate:canvas:${this.config.componentName}:table` as `rotate:canvas:${string}:table`,
        {
          collection: rows,
          count: this.cmsCount ?? rows.length,
          component_id: this.model.id,
          name: this.config.componentName,
          loadPage: (page: number) => this.loadCataloguePage(page),
        }
      );
    } catch {
      /* no-op */
    }
  }

  /**
   * "My filters moved" — the port of `resetCatalogue`
   * (catalogue_select_field.js:1133-1147), which legacy runs at the TOP of
   * `updateFilter` and at the END of `deselectModel`:
   *
   *   await self.cmsPages.reset();                    // rewind to page 1
   *   if (self.catalogueSelector.isOwner(self)) {
   *     self.catalogueSelector.clear();               // drop the stale rows
   *     if (self.catalogueSelector.rotated) self.catalogueSelector.updateCatalogue();
   *   }
   *
   * i.e. a params edit or a Reset re-renders the smart list in place when the
   * list on screen is this field's. The port never had it: `toggleSmartList`
   * was the ONLY sender, so once the list was open the rows were frozen while
   * the count badge beside them kept updating. Measured on the Light of AHU
   * 3540 — Smartlist open at `voltage_id=5&style_id=1` (8 rows), switch Style
   * to Linear and the badge goes 8/399 → 391/399 with the same eight
   * Cylindrical rows under it; Reset takes it to 399/399, same eight rows. The
   * list could only be corrected by closing and re-opening it.
   *
   * The ownership test (legacy's `isOwner` + `rotated`) lives on CompTable's
   * side — it is the half that knows which list is on screen — so this is a
   * cheap synchronous announcement and NOT a fetch: an engine whose list is
   * not showing costs one ignored event. `loadPage` is the same half
   * `toggleSmartList` hands over; `loadCataloguePage` reads `filtersQuery()` at
   * call time, so it is always the live filter.
   *
   * It is sent TWICE per move, and the two halves are independent on purpose:
   *
   *  - `reload: true`, synchronously with the filter write. ⚠ ONE ROUND TRIP
   *    EARLIER THAN LEGACY, deliberately. `resetCatalogue` does run first in
   *    `updateFilter`, but it opens with `await self.cmsPages.reset()`, and
   *    `reset()` defaults `updateCount` to true and awaits `fetchCount`
   *    (cms.components.pages.js:93-102) — so legacy reaches `clear()` only once
   *    the new count is in, and its old rows stay on screen AND CLICKABLE until
   *    then. A click there goes `choose:<name>:master` → `selectModel`, which
   *    merges that row's `paramsFields` back into the filter and silently undoes
   *    the edit that triggered the reload. Dropping them with the write closes
   *    that window; nothing downstream needs the count, because `loadPage`
   *    reads the filters itself.
   *  - `reload: false`, once `refreshCounts` has settled — the pagination
   *    bound, and nothing else, and ONLY when `cmsCount` is known to belong to
   *    the filter that is live now (`countedFilters`). A failed count fetch
   *    leaves the previous filter's number in `cmsCount`, and overlapping
   *    refreshes resolve in request order, so an unchecked read can bound the
   *    list by a filter it is not showing.
   *
   * `counted` is what the receiver falls back on when neither tick produces a
   * number: a `noFetchCount` / `skipFilteredCountOnInit` field never counts at
   * all, and for it page 1 IS the whole list — the bound `toggleSmartList`
   * already gives it with `count: this.cmsCount ?? rows.length`, and legacy's
   * `has_available_pages` with its leading `if (!self.cms_count) return false`.
   * A field that does count and simply failed keeps an unknown bound instead,
   * so a transient 500 cannot hide rows.
   */
  private announceFilterChange(reload: boolean): void {
    if (this.destroyed) return;
    const counted = !this.config.noFetchCount && !this.skipFilteredCountOnInit;
    const fresh = counted && this.countedFilters === this.filtersQuery();
    ahuBoardBus.emit('catalogue:filters-changed', {
      name: this.config.componentName,
      component_id: this.model.id,
      reload,
      counted,
      count: reload || !fresh ? null : this.cmsCount ?? null,
      loadPage: (page: number) => this.loadCataloguePage(page),
    });
  }

  /** §50.2 shim members #4–#8 for this field. */
  toShim(): ShimCatalogueField {
    const engine = this;
    return {
      cmsPages: {
        results_filter: {
          toJSON: () => engine.filters[0].toJSON(),
        },
        get cms_count() {
          return engine.cmsCount ?? 0;
        },
        get cms_full_count() {
          return engine.cmsFullCount ?? 0;
        },
        get cms_count_fetched() {
          return engine.cmsCount !== undefined;
        },
        get cms_full_count_fetched() {
          return engine.cmsFullCount !== undefined;
        },
      },
      filtersQuery: () => engine.filtersQuery(),
      resolveCountGate: (resolved: boolean) => engine.resolveCountGate(resolved),
      resetFilter: (attrs, silent, filterIndex) =>
        engine.resetFilter(attrs, silent ?? false, filterIndex ?? 0),
      deselectModel: () => engine.deselectModel(),
      subscribe: (cb: () => void) => engine.subscribe(cb),
      checkIfThereIsOnlyOneFoundModel: (force?: boolean) =>
        engine.checkIfThereIsOnlyOneFoundModel(force ?? false),
      get selectedModel() {
        return (engine.selectedModel ?? undefined) as
          | Record<string, unknown>
          | undefined;
      },
      catalogueSelector: engine.catalogueSelectorSeam,
    };
  }
}

/**
 * Hook: one engine per config entry (config array is static per form type —
 * stable hook order). Re-renders on engine version bumps.
 */
export function useCatalogueFields(args: {
  configs: CatalogueFieldConfig[];
  model: FormModel | null;
  dispatch: AppDispatch;
  viewRef: { rate: boolean; withRating: boolean; render(): void };
}): Record<string, CatalogueFieldEngine> {
  const { configs, model, dispatch, viewRef } = args;
  const enginesRef = useRef<Record<string, CatalogueFieldEngine> | null>(null);
  const modelRef = useRef<FormModel | null>(null);
  // 6e2b-2 — damper swaps the whole config ARRAY on fan_intake mount
  // (catalogueFieldsIntake, §49.6:200-207): a configs-reference change
  // rebuilds the engines exactly like the legacy initCatalogueFields(fields).
  const configsRef = useRef<CatalogueFieldConfig[] | null>(null);

  if (model && (modelRef.current !== model || configsRef.current !== configs)) {
    if (enginesRef.current) {
      Object.values(enginesRef.current).forEach((e) => e.destroy());
    }
    const engines: Record<string, CatalogueFieldEngine> = {};
    configs.forEach((config) => {
      engines[config.targetField] = new CatalogueFieldEngine({
        config,
        model,
        dispatch,
        viewRef,
      });
    });
    enginesRef.current = engines;
    modelRef.current = model;
    configsRef.current = configs;
  }

  useEffect(
    () => () => {
      if (enginesRef.current) {
        Object.values(enginesRef.current).forEach((e) => e.destroy());
        enginesRef.current = null;
        modelRef.current = null;
        configsRef.current = null;
      }
    },
    []
  );

  const engines = enginesRef.current;

  // One merged subscription across all engines.
  const subscribe = useMemo(
    () => (cb: () => void) => {
      if (!engines) return () => undefined;
      const offs = Object.values(engines).map((e) => e.subscribe(cb));
      return () => offs.forEach((off) => off());
    },
    [engines]
  );
  const getSnapshot = useMemo(
    () => () =>
      engines
        ? Object.values(engines).reduce((sum, e) => sum + e.getVersion(), 0)
        : 0,
    [engines]
  );
  useSyncExternalStore(subscribe, getSnapshot);

  return engines ?? {};
}
